import { get } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import type { IconFlagCode } from '@mylighthouse/prism-ember/types/components/prism/icon-flag';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import {
  CURRENCY_MAPPING,
  type SupportedCurrencies,
} from 'frontend-utils/utils/constants/currency-mapping-constants';
import type {
  ParityManagerFiltersAssignee,
  ParityManagerFiltersMarketplace,
  ParityManagerFiltersRootCause,
  ParityManagerFiltersStatus,
  ParityManagerIssuesummary,
} from 'lumen/models';
import {
  PROMOTION_TYPES,
  type PromotionType,
} from 'lumen/models/parity-strategy/issue';
import {
  type MembershipType,
  PLATFORM_TRANSLATION_KEYS,
} from 'lumen/utils/constants/general-constants';
import {
  type Membership,
  TRANSLATIONS_MEMBERSHIP_TYPES,
} from 'lumen/utils/constants/pi-constant-values';

interface Email {
  hotel: { sent: string };
  wholesaler: { sent: string };
}

export const ISSUE_TYPES = {
  MAJOR_OTA: 1,
  TEST_BOOKING: 2,
  BAIT_AND_SWITCH: 3,
} as const;

export type IssueType = (typeof ISSUE_TYPES)[keyof typeof ISSUE_TYPES];

export const ISSUE_TYPE_TRANSLATIONS: Record<IssueType, string> = {
  [ISSUE_TYPES.MAJOR_OTA]: tKey(
    'parity_manager.add_issue.form.title.major_ota',
  ),
  [ISSUE_TYPES.TEST_BOOKING]: tKey(
    'parity_manager.add_issue.form.title.test_booking',
  ),
  [ISSUE_TYPES.BAIT_AND_SWITCH]: tKey(
    'parity_manager.add_issue.form.title.bait_and_switch',
  ),
} as const;

export const ISSUE_TYPE_PRETTY_NAMES: Record<IssueType, string> = {
  [ISSUE_TYPES.MAJOR_OTA]: 'Issues',
  [ISSUE_TYPES.TEST_BOOKING]: 'Test bookings',
  [ISSUE_TYPES.BAIT_AND_SWITCH]: 'Bait-and-switch',
} as const;

const ENTRY_MODES = {
  OTHER: 0,
  MANUAL: 1,
  HISTORICAL: 2,
  ADMIN: 3,
  PARITY_STRATEGY: 4,
  THIRD_PARTY: 5,
} as const;

export const SOURCES = {
  PMS: 'pms',
  CRS: 'crs',
} as const;

export const SOURCE_PRETTY_NAMES: Record<Source, string> = {
  [SOURCES.PMS]: 'PMS',
  [SOURCES.CRS]: 'CRS',
} as const;

export type Source = (typeof SOURCES)[keyof typeof SOURCES];

type EntryMode = (typeof ENTRY_MODES)[keyof typeof ENTRY_MODES];

const TEST_BOOKING_POS_SUPPORT_DATE = '2026-02-04';

export default class ParityManagerIssue extends Model {
  [Type] = 'parity-manager/issue' as const;

  @belongsTo('parity-manager/filters/assignee', { async: true, inverse: null })
  declare assigneeId: ParityManagerFiltersAssignee | null;

  @belongsTo('parity-manager/issuesummary', { async: false, inverse: null })
  declare issueSummary: ParityManagerIssuesummary | null;

  @belongsTo('parity-manager/filters/marketplace', {
    async: true,
    inverse: null,
  })
  declare marketplace: ParityManagerFiltersMarketplace | null;

  @belongsTo('parity-manager/filters/root-cause', {
    async: true,
    inverse: null,
  })
  declare rootCauseId: ParityManagerFiltersRootCause | null;

  @belongsTo('parity-manager/filters/status', { async: true, inverse: null })
  declare status: ParityManagerFiltersStatus | null;

  @attr('string') declare arrivalDate: string;
  @attr('boolean') declare baseCancellation: boolean;
  @attr('boolean') declare baseCityIncl: boolean | null;
  @attr('boolean') declare baseOtherIncl: boolean | null;
  @attr('number') declare basePlatform: Platform;
  @attr('number') declare baseRate: number;
  @attr('string') declare baseRedirect: string;
  @attr('string') declare baseRoomName: string;
  @attr('string') declare baseShopCurrency: SupportedCurrencies;
  @attr('number') declare baseShopRate: number;
  @attr('boolean') declare baseVatIncl: boolean | null;
  @attr('string') declare bookingId: string;
  @attr('string') declare brandCode: string;
  @attr('boolean') declare channelCancellation: boolean;
  @attr('boolean') declare channelCityIncl: boolean | null;
  @attr('string') declare channelId: string;
  @attr('boolean') declare channelIsMobile: boolean;
  @attr('number') declare channelMembershipType: MembershipType | Membership;
  @attr('boolean') declare channelOtherIncl: boolean | null;
  @attr('number') declare channelPlatform: Platform | undefined;
  @attr('string') declare channelPos: IconFlagCode | null;
  @attr('number') declare channelRate: number;
  @attr('string') declare channelRedirect: string;
  @attr('string') declare channelRoomName: string;
  @attr('string') declare channelShopCurrency: SupportedCurrencies;
  @attr('number') declare channelShopRate: number;
  @attr('boolean') declare channelVatIncl: boolean | null;
  @attr('string') declare comment: string;
  @attr('number') declare differenceWithBaserate: number;
  @attr() declare emails: Email;
  @attr('number') declare entryMode: EntryMode;
  @attr('string') declare extractDate: string;
  @attr('string') declare extranetUrl: string;
  @attr('string') declare guestFirstName: string;
  @attr('string') declare guestLastName: string;
  @attr('string') declare hotelName: string;
  @attr('number') declare issueType: IssueType;
  @attr('string') declare liveUrl: string;
  @attr('number') declare los: number;
  @attr('boolean') declare metaCancellation: boolean;
  @attr('string') declare metaId: string;
  @attr('number') declare metaRate: number;
  @attr('string') declare metaRoomName: string;
  @attr('string') declare metasearchRedirect: string;
  @attr('string') declare parityStrategyPromotionType: PromotionType;
  @attr('string') declare ratePlan: string;
  @attr('string') declare screenshotsUrl: string;
  @attr('string') declare source: Source | null;
  @attr('boolean') declare subscriptionCityIncl: boolean | null;
  @attr('string') declare subscriptionCurrency: SupportedCurrencies;
  @attr('number') declare subscriptionId: number;
  @attr('boolean') declare subscriptionOtherIncl: boolean | null;
  @attr('boolean') declare subscriptionVatIncl: boolean | null;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];

  hasChannelPlatform = (): this is this & { channelPlatform: Platform } => {
    return this.channelPlatform !== undefined;
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseFormattedShoppedRate() {
    return this.formatRateWithCustomSymbol(
      this.baseShopCurrency,
      this.baseShopRate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseTaxInclText() {
    return this.formatTaxesText(
      this.baseVatIncl,
      this.baseCityIncl,
      this.baseOtherIncl,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get channelFormattedShoppedRate() {
    return this.formatRateWithCustomSymbol(
      this.channelShopCurrency,
      this.channelShopRate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get channelTaxInclText() {
    return this.formatTaxesText(
      this.channelVatIncl,
      this.channelCityIncl,
      this.channelOtherIncl,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currencySymbol() {
    return CURRENCY_MAPPING[this.subscriptionCurrency];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get customIssueTitle() {
    if (!this.isParityStrategyEntry) {
      return null;
    }

    let issueTypeDetails = null;
    if (this.parityStrategyPromotionType === PROMOTION_TYPES.POS_DISCOUNT) {
      issueTypeDetails = this.intl.t('general.pos_short');
    } else if (
      this.parityStrategyPromotionType === PROMOTION_TYPES.LOS_DISCOUNT
    ) {
      issueTypeDetails = this.intl.t('general.los', { los: this.los });
    } else if (
      this.parityStrategyPromotionType === PROMOTION_TYPES.MOBILE_DISCOUNT
    ) {
      issueTypeDetails = this.formattedChannelPlatform;
    } else if (
      this.parityStrategyPromotionType === PROMOTION_TYPES.MEMBERSHIP_DISCOUNT
    ) {
      issueTypeDetails = this.intl.t(
        TRANSLATIONS_MEMBERSHIP_TYPES[this.channelMembershipType] as string,
      );
    }

    return issueTypeDetails
      ? `${this.intl.t('parity.parity_strategy_issue')} - ${issueTypeDetails}`
      : this.intl.t('parity.parity_strategy_issue');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get daysSinceShopped() {
    return dayjs().diff(this.extractDateObject, 'day');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get differenceWithBaserateClass() {
    if (this.differenceWithBaserate < 0) {
      return 'text-critical-default';
    }

    if (this.differenceWithBaserate > 0) {
      return 'text-success-default';
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get extractDateObject() {
    return dayjs(this.extractDate);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedAbsoluteDifferenceWithBaserate() {
    return `${this.currencySymbol} ${this.hotelAndMarketService.formatRate(
      Math.abs(this.differenceWithBaserate),
      { includeCurrencySymbol: false },
    )}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedArrivalDate() {
    return dayjs(this.arrivalDate).format('L');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedBasePlatform() {
    return this.intl.t(PLATFORM_TRANSLATION_KEYS[this.basePlatform]);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedBaseRate() {
    return `${this.currencySymbol} ${this.hotelAndMarketService.formatRate(
      Math.abs(this.baseRate),
      { includeCurrencySymbol: false },
    )}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChannel() {
    return this.ota.otaIdToPrettyName(this.channelId);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChannelPlatform() {
    if (!this.hasChannelPlatform()) {
      return this.intl.t('general.missing');
    }

    return this.intl.t(PLATFORM_TRANSLATION_KEYS[this.channelPlatform]);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChannelPos() {
    if (this.channelPos !== null) {
      return this.channelPos;
    }

    const isPosSupportedForTestBooking =
      this.isTestBookingIssue &&
      this.extractDateObject.isAfter(TEST_BOOKING_POS_SUPPORT_DATE);
    if (isPosSupportedForTestBooking) {
      return this.intl.t('general.not_applicable');
    }

    return this.intl.t('general.missing');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChannelRate() {
    const rate = `${this.currencySymbol} ${this.hotelAndMarketService.formatRate(
      Math.abs(this.channelRate),
      { includeCurrencySymbol: false },
    )}`;

    return rate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedExtractDate() {
    return this.extractDateObject.format('L');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMetaRate() {
    return `${this.currencySymbol} ${this.hotelAndMarketService.formatRate(
      Math.abs(this.metaRate),
      { includeCurrencySymbol: false },
    )}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRootCause() {
    if (!this.hasRootCause) {
      return this.intl.t('general.missing');
    }

    // eslint-disable-next-line ember/no-get
    return get(this.rootCauseId, 'formattedName');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedStatus() {
    if (!this.hasStatus) {
      return this.intl.t('general.missing');
    }

    // eslint-disable-next-line ember/no-get
    return get(this.status, 'formattedName');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasAssignee() {
    return this.assigneeId?.id !== null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasExtranetUrl() {
    return this.extranetUrl !== null && this.extranetUrl !== undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasLiveViewUrl() {
    return this.liveUrl !== null && this.liveUrl !== undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasRootCause() {
    return this.rootCauseId?.id !== null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasScreenshotsUrl() {
    return this.screenshotsUrl !== null && this.screenshotsUrl !== undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasStatus() {
    return this.status?.id !== null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBaitAndSwitchIssue() {
    return this.issueType === ISSUE_TYPES.BAIT_AND_SWITCH;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMailSentToHotel() {
    return this.emails?.hotel?.sent;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMailSentToWholesaler() {
    return this.emails?.wholesaler?.sent;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMajorOTAIssue() {
    return this.issueType === ISSUE_TYPES.MAJOR_OTA;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isManualEntry() {
    return this.entryMode === ENTRY_MODES.MANUAL;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNormalized() {
    return this.baseRate === this.baseShopRate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isParityStrategyEntry() {
    return this.entryMode === ENTRY_MODES.PARITY_STRATEGY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTestBookingIssue() {
    return this.issueType === ISSUE_TYPES.TEST_BOOKING;
  }

  // Taxes
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subscriptionTaxInclText() {
    return this.formatTaxesText(
      this.subscriptionVatIncl,
      this.subscriptionCityIncl,
      this.subscriptionOtherIncl,
    );
  }

  // Workaround for formatting a rate with a custom currency: fetch the currency symbol + format the rate with includeSymbol=false
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRateWithCustomSymbol(
    currencyCode: keyof typeof CURRENCY_MAPPING,
    rate: number,
  ) {
    return `${CURRENCY_MAPPING[currencyCode]} ${this.hotelAndMarketService.formatRate(
      rate,
      { includeCurrencySymbol: false },
    )}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatTaxesText(
    isVatInclusive: boolean | null,
    isCityTaxInclusive: boolean | null,
    isOtherTaxInclusive: boolean | null,
  ) {
    const taxesToVisualise = [];

    if (isVatInclusive !== null) {
      taxesToVisualise.push(
        isVatInclusive
          ? this.intl.t('general.tax_included.short', { type: 'VAT' })
          : this.intl.t('general.tax_excluded.short', { type: 'VAT' }),
      );
    }
    if (isCityTaxInclusive !== null) {
      taxesToVisualise.push(
        isCityTaxInclusive
          ? this.intl.t('general.tax_included.short', { type: 'City' })
          : this.intl.t('general.tax_excluded.short', { type: 'City' }),
      );
    }
    if (isOtherTaxInclusive !== null) {
      taxesToVisualise.push(
        isOtherTaxInclusive
          ? this.intl.t('general.tax_included.short', { type: 'Other' })
          : this.intl.t('general.tax_excluded.short', { type: 'Other' }),
      );
    }

    return taxesToVisualise.join(', ');
  }
}
